import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import { useWorkbenchStore } from '@presentation/stores/workbenchStore';
import { buildDocumentPayload } from '@presentation/utils/apiRequestPayload';
import type {
  ApiRequestDocumentPayload,
  CreateApiRequestPayload,
} from '@core/repositories/ApiRequestRepository';

export const useApiRequests = (collectionId: string | undefined, folderId?: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.apiRequests(collectionId ?? '', folderId),
    queryFn: () => container.apiRequests.list(collectionId as string, folderId),
    enabled: Boolean(collectionId),
  });
};

export const useApiRequest = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.apiRequest(id ?? ''),
    queryFn: () => container.apiRequests.get(id as string),
    enabled: Boolean(id),
  });
};

export const useCreateApiRequest = (collectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateApiRequestPayload) =>
      container.apiRequests.create(collectionId, payload),
    onSuccess: (request) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.apiRequests(collectionId, request.folderId),
      });
    },
  });
};

export const useUpdateApiRequest = (id: string) => {
  const queryClient = useQueryClient();
  const syncTabName = useWorkbenchStore((state) => state.syncTabName);

  return useMutation({
    mutationFn: (payload: ApiRequestDocumentPayload) => container.apiRequests.update(id, payload),
    onSuccess: (request) => {
      queryClient.setQueryData(QUERY_KEYS.apiRequest(id), request);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.apiRequests(request.collectionId, request.folderId),
      });
      // This hook is used for out-of-tab edits (sidebar rename) — an
      // already-open tab for this request otherwise keeps the stale name and
      // would silently revert the rename the next time it's saved.
      syncTabName(request.id, request.name);
    },
  });
};

/**
 * Renames a request from outside its editor tab (sidebar context menu,
 * double-click). The update endpoint validates and persists the request as
 * a whole document — `name`/`method`/`url` are all required, and
 * params/headers/body/variables are wholesale-replaced from whatever the
 * payload contains (see ApiRequestData's docblock) — so a naive `{ name }`
 * payload either fails validation outright or, if the backend ever relaxed
 * that requirement, would silently wipe every other field back to empty.
 * The tree only holds a summary entity (no params/headers/body/scripts —
 * see RequestTreeNode.handleOpen), so the full document is fetched fresh
 * before resubmitting it with just the name swapped in.
 */
export const useRenameApiRequest = (id: string) => {
  const queryClient = useQueryClient();
  const update = useUpdateApiRequest(id);

  return useMutation({
    mutationFn: async (name: string) => {
      const full = await queryClient.fetchQuery({
        queryKey: QUERY_KEYS.apiRequest(id),
        queryFn: () => container.apiRequests.get(id),
      });
      return update.mutateAsync(buildDocumentPayload({ ...full, name }));
    },
  });
};

export const useDuplicateApiRequest = (collectionId: string, folderId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.apiRequests.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.apiRequests(collectionId, folderId) });
    },
  });
};

export const useMoveApiRequest = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, position }: { folderId: string | null; position: number }) =>
      container.apiRequests.move(id, folderId, position),
    onSuccess: (request) => {
      queryClient.setQueryData(QUERY_KEYS.apiRequest(id), request);
      queryClient.invalidateQueries({
        queryKey: ['collections', request.collectionId, 'requests'],
      });
    },
  });
};

export const useReorderApiRequests = (collectionId: string, folderId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: string[]) => container.apiRequests.reorder(collectionId, orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.apiRequests(collectionId, folderId) });
    },
  });
};

export const useDeleteApiRequest = (collectionId: string, folderId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.apiRequests.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.apiRequests(collectionId, folderId) });
    },
  });
};

export const useToggleApiRequestFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? container.apiRequests.unfavorite(id) : container.apiRequests.favorite(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.apiRequest(id) });
    },
  });
};

/** Fires the Send button. `overrides` carries unsaved editor state, if any. */
export const useSendApiRequest = (id: string, workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (overrides?: ApiRequestDocumentPayload) =>
      container.apiRequests.send(id, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history(workspaceId) });
    },
  });
};

/** Parses a pasted cURL command into request fields for the URL bar's quick-fill. */
export const useParseCurl = () => {
  return useMutation({
    mutationFn: (command: string) => container.apiRequests.parseCurl(command),
  });
};
